/**
 * VenueFlow FileList Component
 * 
 * Displays uploaded files with progress bars and cancel buttons.
 */

import React from "react";

// ============================================================
// STYLES
// ============================================================
const styles = {
  container: {
    marginTop: 10,
    fontSize: 13,
    color: "#111827",
  },
  fileItem: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    background: "#f9fafb",
    padding: "8px 10px",
    borderRadius: 6,
    marginBottom: 6,
    border: "1px solid #e5e7eb",
  },
  fileItemHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  fileName: {
    fontSize: 13,
    color: "#111827",
    wordBreak: "break-word",
    flex: 1,
  },
  fileNameSuccess: {
    color: "#059669",
  },
  fileNameValidating: {
    color: "#92400e",
  },
  progressBarOuter: {
    width: "100%",
    height: 6,
    background: "#e5e7eb",
    borderRadius: 4,
    overflow: "hidden",
  },
  progressBarFill: {
    height: "100%",
    background: "linear-gradient(90deg, #373737, #2fd6b5)",
    transition: "width 0.2s ease-in-out",
  },
  validatingBarOuter: {
    width: "100%",
    height: 6,
    background: "#e5e7eb",
    borderRadius: 4,
    overflow: "hidden",
    position: "relative",
  },
  validatingBarFill: {
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "40%",
    background: "linear-gradient(90deg, transparent, #f59e0b, transparent)",
    borderRadius: 4,
    animation: "validatingSlide 1.4s ease-in-out infinite",
  },
  validatingLabel: {
    fontSize: 11,
    color: "#92400e",
    fontStyle: "italic",
  },
  cancelBtn: {
    background: "transparent",
    border: "none",
    color: "#2fd6b5",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: 14,
    padding: "2px 6px",
    borderRadius: 4,
  },
  cancelBtnHover: {
    background: "rgba(47, 214, 181, 0.1)",
  },
};

// Inject keyframes for the validating animation (once)
if (typeof document !== "undefined" && !document.getElementById("vf-validating-keyframes")) {
  const styleEl = document.createElement("style");
  styleEl.id = "vf-validating-keyframes";
  styleEl.textContent = `
    @keyframes validatingSlide {
      0%   { left: -40%; }
      100% { left: 100%; }
    }
  `;
  document.head.appendChild(styleEl);
}

// ============================================================
// FILE ITEM
// ============================================================
function FileItem({ file, progress, onCancel }) {
  const [hovered, setHovered] = React.useState(false);
  const isComplete = file.status === "complete";
  const isUploading = file.status === "uploading";
  const isValidating = file.status === "validating";

  return (
    <div style={styles.fileItem}>
      <div style={styles.fileItemHeader}>
        <span
          style={{
            ...styles.fileName,
            ...(isComplete ? styles.fileNameSuccess : {}),
            ...(isValidating ? styles.fileNameValidating : {}),
          }}
        >
          {isComplete ? "✔ " : ""}
          {isValidating ? "⏳ " : ""}
          {file.displayName || file.prefixedFileName || file.originalFileName}
        </span>

        {!isComplete && (
          <button
            type="button"
            style={{
              ...styles.cancelBtn,
              ...(hovered ? styles.cancelBtnHover : {}),
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={() => onCancel(file.id)}
          >
            ✖
          </button>
        )}
      </div>

      {isValidating && (
        <>
          <div style={styles.validatingBarOuter}>
            <div style={styles.validatingBarFill} />
          </div>
          <span style={styles.validatingLabel}>
            Checking specs{file.file?.size > 100 * 1024 * 1024
              ? ` (${file.file.size > 1024 * 1024 * 1024
                  ? (file.file.size / (1024 * 1024 * 1024)).toFixed(1) + "GB"
                  : (file.file.size / (1024 * 1024)).toFixed(0) + "MB"})…`
              : "…"}
          </span>
        </>
      )}

      {isUploading && (
        <div style={styles.progressBarOuter}>
          <div
            style={{
              ...styles.progressBarFill,
              width: `${progress || 0}%`,
            }}
          />
        </div>
      )}
    </div>
  );
}

// ============================================================
// FILE LIST COMPONENT
// ============================================================
export function FileList({ files, progress, onCancel }) {
  if (!files || files.length === 0) return null;

  return (
    <div style={styles.container}>
      {files.map((file) => (
        <FileItem
          key={file.id}
          file={file}
          progress={progress?.[file.id]}
          onCancel={onCancel}
        />
      ))}
    </div>
  );
}

export default FileList;
